import { useState } from 'react';
import { Badge, Menu, MenuItem } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import SwapVert from '@mui/icons-material/SwapVert';
import { Tooltip } from 'framework/ui/mui-builder';
import { SortOrder, useListContext } from 'framework/list';

type ListSortButtonOption = {
  source: string;
  label: string;
};

type ListSortButtonProps = {
  buttonId: string;
  options: ListSortButtonOption[];
  title?: string;
};

const ListSortButton = ({ buttonId, options, title = 'Sorting' }: ListSortButtonProps) => {
  const { sort, sortBy } = useListContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onMenuItemClick = (source: string, order: SortOrder) => {
    sortBy?.(source, order);
    handleClose();
  };

  const isSelected = (source: string, order: SortOrder) => sort?.field === source && sort?.order === order;

  if (options.length === 0) return null;

  return (
    <>
      <Tooltip title={title}>
        <IconButton
          id={buttonId}
          aria-controls={open ? `${buttonId}-menu` : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          color="default"
        >
          <Badge variant="dot" color="primary" invisible={!sort?.field}>
            <SwapVert />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        id={`${buttonId}-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': buttonId,
          },
        }}
      >
        {options.flatMap(({ source, label }) => [
          <MenuItem
            disabled={isSelected(source, 'ASC')}
            key={`${source}-ASC`}
            onClick={() => onMenuItemClick(source, 'ASC')}
          >
            By {label} in ascending order
          </MenuItem>,
          <MenuItem
            disabled={isSelected(source, 'DESC')}
            key={`${source}-DESC`}
            onClick={() => onMenuItemClick(source, 'DESC')}
          >
            By {label} in descending order
          </MenuItem>,
        ])}
        {sort?.field ? (
          <MenuItem key="reset" onClick={() => onMenuItemClick('', '' as SortOrder)}>
            Reset
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
};

export default ListSortButton;
